import { useCallback, useEffect, useRef, useState } from "react";
import { getApi } from "../services/api/globalApi";

// Each table owns its request state. Only its latest response may update rows.
export default function useTableRequest({ initialLoading = true } = {}) {
  const [status, setStatus] = useState(initialLoading ? "loading" : "idle");
  const sequence = useRef(0);
  const mounted = useRef(true);
  const lastRequest = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, []);

  const load = useCallback(async (options, onSuccess) => {
    const requestId = ++sequence.current;
    lastRequest.current = { options, onSuccess };
    setStatus("loading");
    try {
      const response = await getApi(options);
      if (!mounted.current || requestId !== sequence.current) return;
      onSuccess(response);
      setStatus("success");
    } catch (error) {
      if (mounted.current && requestId === sequence.current) setStatus("error");
    }
  }, []);

  const retry = useCallback(() => {
    if (lastRequest.current) {
      const { options, onSuccess } = lastRequest.current;
      return load(options, onSuccess);
    }
  }, [load]);

  return { load, status, loading: status === "loading", error: status === "error", retry };
}
